// src/services/invoice/invoiceService.ts

/**
 * Service für Rechnungs-CRUD-Operationen
 * 
 * Verantwortlich für:
 * - Automatische Entwurfserstellung nach Finalisierung
 * - Laden/Speichern/Aktualisieren von Rechnungen
 * - Rechnungsnummernvergabe bei Freigabe
 * - Betragsberechnung
 */

import { supabase } from '../../lib/supabaseClient';
import { netlifyFetch } from '../../shared/services/netlifyFetch';
import { 
  Invoice, 
  InvoiceLineItem, 
  InvoiceLineItemInput, 
  InvoiceSettings, 
  CustomerPricing,
  CreateInvoiceDraftInput 
} from '../../features/invoices/types';

/**
 * Ermittelt die Person, die im Empfängerblock der Rechnung erscheint.
 *
 * Vorrangregel: Ist am Kunden ein abweichender Rechnungsempfänger gepflegt
 * (rechnung_empfaenger_nachname), gilt dieser. Andernfalls bleibt es beim
 * bisherigen Verhalten, also beim Ansprechpartner des Kunden mit der aus
 * geschlecht abgeleiteten Anrede.
 *
 * Wird hier exportiert, damit die DATEV-Weiterberechnung dieselbe Regel
 * verwendet und die Logik nicht zweimal im Frontend existiert. Die
 * Netlify-Functions (send-invoice, send-payment-reminder) führen aus
 * Laufzeitgründen eigene, inhaltsgleiche Kopien.
 */
export const buildRechnungsEmpfaengerPerson = (
  customer: any
): { anrede: string | null; vorname: string | null; nachname: string | null } => {
  const abweichenderNachname = (customer?.rechnung_empfaenger_nachname || '').trim();

  if (abweichenderNachname) {
    return {
      anrede: customer.rechnung_empfaenger_anrede || null,
      vorname: (customer.rechnung_empfaenger_vorname || '').trim() || null,
      nachname: abweichenderNachname
    };
  }

  return {
    anrede: customer?.geschlecht === 'weiblich'
      ? 'Frau'
      : customer?.geschlecht === 'männlich'
        ? 'Herr'
        : null,
    vorname: customer?.vorname || null,
    nachname: customer?.nachname || null
  };
};

export const invoiceService = {

  // ============================================
  // SETTINGS
  // ============================================

  /**
   * Lädt die XTAX Rechnungseinstellungen
   */
  getSettings: async (): Promise<InvoiceSettings | null> => {
    const { data, error } = await supabase
      .from('invoice_settings')
      .select('*')
      .limit(1)
      .single();

    if (error) {
      console.error('[InvoiceService] Fehler beim Laden der Einstellungen:', error);
      return null;
    }
    return data as InvoiceSettings;
  },

  // ============================================
  // CUSTOMER PRICING
  // ============================================

  /**
   * Lädt den aktuell gültigen Preis pro Arbeitnehmer für einen Kunden
   */
  getCurrentPricing: async (customerId: string): Promise<CustomerPricing | null> => {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('customer_pricing')
      .select('*')
      .eq('customer_id', customerId)
      .lte('gueltig_ab', today)
      .or(`gueltig_bis.is.null,gueltig_bis.gte.${today}`)
      .order('gueltig_ab', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[InvoiceService] Fehler beim Laden des Preises:', error);
      return null;
    }
    return data as CustomerPricing | null;
  },

  /**
   * Speichert oder aktualisiert den Preis pro Arbeitnehmer für einen Kunden.
   * 
   * Prüft zuerst, ob für heute bereits ein Eintrag existiert (Update),
   * ansonsten wird ein neuer Eintrag erstellt (Insert).
   * Kein upsert/onConflict nötig – vermeidet fehlendes UNIQUE-Constraint-Problem.
   */
  savePricing: async (customerId: string, preisProArbeitnehmer: number): Promise<boolean> => {
    const today = new Date().toISOString().split('T')[0];

    try {
      // Prüfe ob für heute bereits ein Eintrag existiert
      const { data: existing } = await supabase
        .from('customer_pricing')
        .select('id')
        .eq('customer_id', customerId)
        .eq('gueltig_ab', today)
        .maybeSingle();

      if (existing) {
        // Update bestehenden Eintrag
        const { error } = await supabase
          .from('customer_pricing')
          .update({
            preis_pro_arbeitnehmer: preisProArbeitnehmer,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (error) {
          console.error('[InvoiceService] Fehler beim Aktualisieren des Preises:', error);
          return false;
        }
      } else {
        // Alten Eintrag abschließen (gueltig_bis auf gestern setzen)
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        await supabase
          .from('customer_pricing')
          .update({ gueltig_bis: yesterdayStr })
          .eq('customer_id', customerId)
          .is('gueltig_bis', null);

        // Neuen Eintrag erstellen
        const { error } = await supabase
          .from('customer_pricing')
          .insert({
            customer_id: customerId,
            preis_pro_arbeitnehmer: preisProArbeitnehmer,
            gueltig_ab: today
          });

        if (error) {
          console.error('[InvoiceService] Fehler beim Speichern des Preises:', error);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('[InvoiceService] Unerwarteter Fehler beim Speichern des Preises:', error);
      return false;
    }
  },

  // ============================================
  // INVOICES - CRUD
  // ============================================

  /**
   * Lädt alle Rechnungen mit optionalem Filter
   */
  getInvoices: async (filter?: { status?: string; year?: number; month?: number }): Promise<Invoice[]> => {
    let query = supabase
      .from('invoices')
      .select(`
        *,
        line_items:invoice_line_items(*),
        customer:customers(company, mandantennummer, email, rechnungs_email, reverse_charge, ust_id_kunde)
      `)
      .order('created_at', { ascending: false });

    if (filter?.status) {
      query = query.eq('status', filter.status);
    } else {
      // Importierte Rechnungen im operativen Dashboard ausblenden
      query = query.neq('status', 'importiert');
    }
    if (filter?.year) {
      query = query.eq('year', filter.year);
    }
    if (filter?.month) {
      query = query.eq('month', filter.month);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[InvoiceService] Fehler beim Laden der Rechnungen:', error);
      return [];
    }
    return (data || []) as Invoice[];
  },

  /**
   * Lädt eine einzelne Rechnung mit Positionen
   */
  getInvoice: async (invoiceId: string): Promise<Invoice | null> => {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        line_items:invoice_line_items(*),
        customer:customers(company, mandantennummer, email, rechnungs_email, reverse_charge, ust_id_kunde)
      `)
      .eq('id', invoiceId)
      .single();

    if (error) {
      console.error('[InvoiceService] Fehler beim Laden der Rechnung:', error);
      return null;
    }
    return data as Invoice;
  },

  /**
   * Lädt die zeitlich letzte festgeschriebene Rechnung eines Kunden.
   *
   * Wird im Entwurfs-Detailview verwendet, um vor dem Finalisieren die
   * Vorgängerrechnung (Positionen, Beträge, PDF) zum visuellen Abgleich
   * anzuzeigen.
   *
   * Kriterien:
   * - Gleicher customer_id wie der aktuelle Entwurf (bei Sammelrechnungen
   *   automatisch beim Auftraggeber).
   * - Status in ('freigegeben', 'versendet', 'importiert') – Altdaten zählen
   *   mit, damit kurz nach Migration trotzdem Vergleichswerte da sind.
   * - Die aktuell geöffnete Rechnung selbst wird ausgeschlossen.
   * - Sortierung absteigend nach year, month, rechnungsdatum (NULLS LAST),
   *   created_at – nimmt also die zeitlich jüngste passende Rechnung.
   *
   * Liefert die Rechnung inklusive Positionen und ausgewählter Kundenfelder
   * (analog zu getInvoice), oder null wenn keine Vorgängerrechnung existiert.
   */
  getLastFinalizedInvoiceForCustomer: async (
    customerId: string,
    excludeInvoiceId: string
  ): Promise<Invoice | null> => {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        line_items:invoice_line_items(*),
        customer:customers(company, mandantennummer, email, rechnungs_email, reverse_charge, ust_id_kunde)
      `)
      .eq('customer_id', customerId)
      .in('status', ['freigegeben', 'versendet', 'importiert'])
      .neq('id', excludeInvoiceId)
      .order('year', { ascending: false })
      .order('month', { ascending: false })
      .order('rechnungsdatum', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[InvoiceService] Fehler beim Laden der Vorgängerrechnung:', error);
      return null;
    }
    return data as Invoice | null;
  },

  /**
   * Aktualisiert eine Rechnung (nur Entwürfe)
   */
  updateInvoice: async (invoiceId: string, updates: Partial<Invoice>): Promise<boolean> => {
    const { error } = await supabase
      .from('invoices')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', invoiceId)
      .eq('status', 'entwurf'); // Sicherheit: Nur Entwürfe bearbeitbar

    if (error) {
      console.error('[InvoiceService] Fehler beim Aktualisieren:', error);
      return false;
    }
    return true;
  },

  /**
   * Löscht einen Rechnungsentwurf
   */
  deleteDraft: async (invoiceId: string): Promise<boolean> => {
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', invoiceId)
      .eq('status', 'entwurf'); // Sicherheit: Nur Entwürfe löschbar

    if (error) {
      console.error('[InvoiceService] Fehler beim Löschen:', error);
      return false;
    }
    return true;
  },

  /**
   * Löscht einen Rechnungsentwurf anhand von Kunde/Jahr/Monat
   * 
   * Wird beim Zurücksetzen eines Monats aufgerufen.
   * Löscht NUR Rechnungen mit Status 'entwurf' – freigegebene/versendete bleiben unangetastet.
   */
  deleteDraftByCustomerMonth: async (customerId: string, year: number, month: number): Promise<{ deleted: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .delete()
        .eq('customer_id', customerId)
        .eq('year', year)
        .eq('month', month)
        .eq('status', 'entwurf')
        .select('id');

      if (error) {
        console.error('[InvoiceService] Fehler beim Löschen des Entwurfs:', error);
        return { deleted: false, error: error.message };
      }

      if (data && data.length > 0) {
        console.log(`[InvoiceService] Rechnungsentwurf gelöscht für Kunde ${customerId}, ${month}/${year}`);
        return { deleted: true };
      }

      return { deleted: false }; // Kein Entwurf gefunden (normal, z.B. bereits freigegeben)
    } catch (error) {
      console.error('[InvoiceService] Unerwarteter Fehler beim Löschen:', error);
      return { deleted: false, error: error instanceof Error ? error.message : 'Unbekannter Fehler' };
    }
  },

  // ============================================
  // LINE ITEMS
  // ============================================

  /**
   * Fügt eine Position hinzu und berechnet Rechnungsbeträge neu
   */
  addLineItem: async (invoiceId: string, item: InvoiceLineItemInput): Promise<InvoiceLineItem | null> => {
    // Nächste Positionsnummer ermitteln
    const { data: existingItems } = await supabase
      .from('invoice_line_items')
      .select('position')
      .eq('invoice_id', invoiceId)
      .order('position', { ascending: false })
      .limit(1);

    const nextPosition = existingItems && existingItems.length > 0 
      ? existingItems[0].position + 1 
      : 1;

    const gesamtpreis = Math.round(item.menge * item.einzelpreis * 100) / 100;

    const { data, error } = await supabase
      .from('invoice_line_items')
      .insert({
        invoice_id: invoiceId,
        position: nextPosition,
        beschreibung: item.beschreibung,
        menge: item.menge,
        einheit: item.einheit,
        einzelpreis: item.einzelpreis,
        gesamtpreis: gesamtpreis
      })
      .select()
      .single();

    if (error) {
      console.error('[InvoiceService] Fehler beim Hinzufügen der Position:', error);
      return null;
    }

    // Rechnungsbeträge neu berechnen
    await invoiceService.recalculateInvoiceTotals(invoiceId);

    return data as InvoiceLineItem;
  },

  /**
   * Aktualisiert eine Position
   */
  updateLineItem: async (itemId: string, updates: Partial<InvoiceLineItemInput>): Promise<boolean> => {
    // Aktuelle Daten laden
    const { data: currentItem } = await supabase
      .from('invoice_line_items')
      .select('*')
      .eq('id', itemId)
      .single();

    if (!currentItem) return false;

    const menge = updates.menge ?? currentItem.menge;
    const einzelpreis = updates.einzelpreis ?? currentItem.einzelpreis;
    const gesamtpreis = Math.round(menge * einzelpreis * 100) / 100;

    const { error } = await supabase
      .from('invoice_line_items')
      .update({
        ...updates,
        gesamtpreis,
        updated_at: new Date().toISOString()
      })
      .eq('id', itemId);

    if (error) {
      console.error('[InvoiceService] Fehler beim Aktualisieren der Position:', error);
      return false;
    }

    // Rechnungsbeträge neu berechnen
    await invoiceService.recalculateInvoiceTotals(currentItem.invoice_id);

    return true;
  },

  /**
   * Löscht eine Position
   *
   * Nach dem DELETE werden alle verbleibenden Positionen lückenlos auf 1..N
   * umnummeriert. So entstehen im Entwurf keine sichtbaren Lücken in der
   * Detailansicht oder in der Entwurfs-PDF-Vorschau. Das finale Renumbering
   * vor der Festschreibung bleibt als Sicherheitsnetz bestehen.
   */
  deleteLineItem: async (itemId: string, invoiceId: string): Promise<boolean> => {
    const { error } = await supabase
      .from('invoice_line_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      console.error('[InvoiceService] Fehler beim Löschen der Position:', error);
      return false;
    }

    // Verbleibende Positionen lückenlos umnummerieren. Schlägt das fehl, wird das
    // protokolliert; das Löschen selbst gilt als erfolgreich, die Rechnung würde
    // beim Festschreiben ohnehin noch einmal renumbered.
    const renumberResult = await invoiceService.renumberLineItemsSequentially(invoiceId);
    if (!renumberResult.success) {
      console.error('[InvoiceService] Renumbering nach Löschen fehlgeschlagen:', renumberResult.error);
    }

    // Rechnungsbeträge neu berechnen
    await invoiceService.recalculateInvoiceTotals(invoiceId);

    return true;
  },

  // ============================================
  // BERECHNUNG
  // ============================================

  /**
   * Berechnet Netto/USt/Brutto aus allen Positionen und aktualisiert die Rechnung
   */
  recalculateInvoiceTotals: async (invoiceId: string): Promise<void> => {
    const { data: items } = await supabase
      .from('invoice_line_items')
      .select('gesamtpreis')
      .eq('invoice_id', invoiceId);

    const nettoBetrag = (items || []).reduce((sum, item) => sum + Number(item.gesamtpreis), 0);
    const roundedNetto = Math.round(nettoBetrag * 100) / 100;

    // USt-Satz aus Rechnung laden
    const { data: invoice } = await supabase
      .from('invoices')
      .select('ust_satz')
      .eq('id', invoiceId)
      .single();

    const ustSatz = invoice ? Number(invoice.ust_satz) : 19;
    const ustBetrag = Math.round(roundedNetto * ustSatz) / 100;
    const bruttoBetrag = Math.round((roundedNetto + ustBetrag) * 100) / 100;

    await supabase
      .from('invoices')
      .update({
        netto_betrag: roundedNetto,
        ust_betrag: ustBetrag,
        brutto_betrag: bruttoBetrag,
        updated_at: new Date().toISOString()
      })
      .eq('id', invoiceId);
  },

  /**
   * HELPER: Verschiebt alle bestehenden Positionen um +1 nach unten und fügt
   * eine neue Lohnabrechnungs-Position an Position 1 ein.
   *
   * Stellt sicher, dass die Lohnabrechnung immer als Position 1 in der Rechnung
   * erscheint – auch wenn vorher bereits andere Positionen (z.B. aus der
   * DATEV-Weiterberechnung) angelegt wurden.
   *
   * Hinweis: Verschiebung erfolgt rückwärts (höchste Position zuerst), um auch
   * ohne UNIQUE-Constraint einen konsistenten Zwischenzustand zu garantieren.
   * Berechnet anschließend die Rechnungsbeträge neu.
   */
  insertLohnPositionAtTop: async (
    invoiceId: string,
    beschreibung: string,
    menge: number,
    einzelpreis: number
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const gesamtpreis = Math.round(menge * einzelpreis * 100) / 100;

      // 1) Bestehende Positionen rückwärts (höchste zuerst) +1 verschieben
      const { data: existingPositions, error: loadError } = await supabase
        .from('invoice_line_items')
        .select('id, position')
        .eq('invoice_id', invoiceId)
        .order('position', { ascending: false });

      if (loadError) {
        console.error('[InvoiceService] Fehler beim Laden bestehender Positionen:', loadError);
        return { success: false, error: 'Bestehende Positionen konnten nicht geladen werden' };
      }

      if (existingPositions && existingPositions.length > 0) {
        for (const item of existingPositions) {
          const { error: updateError } = await supabase
            .from('invoice_line_items')
            .update({
              position: item.position + 1,
              updated_at: new Date().toISOString()
            })
            .eq('id', item.id);

          if (updateError) {
            console.error('[InvoiceService] Fehler beim Verschieben der Position:', updateError);
            return { success: false, error: 'Bestehende Positionen konnten nicht verschoben werden' };
          }
        }
      }

      // 2) Lohn-Position an Position 1 einfügen
      const { error: insertError } = await supabase
        .from('invoice_line_items')
        .insert({
          invoice_id: invoiceId,
          position: 1,
          beschreibung,
          menge,
          einheit: 'Stk',
          einzelpreis,
          gesamtpreis
        });

      if (insertError) {
        console.error('[InvoiceService] Fehler beim Einfügen der Lohn-Position an Position 1:', insertError);
        return { success: false, error: 'Lohn-Position konnte nicht eingefügt werden' };
      }

      // 3) Beträge neu berechnen
      await invoiceService.recalculateInvoiceTotals(invoiceId);

      return { success: true };
    } catch (error) {
      console.error('[InvoiceService] Unerwarteter Fehler in insertLohnPositionAtTop:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  // ============================================
  // POSITIONEN AUS LOHNJOURNAL ERGÄNZEN
  // ============================================

  /**
   * Ergänzt einen bestehenden Rechnungsentwurf um eine Lohnabrechnungs-Position
   * basierend auf den extrahierten Daten aus einem DATEV-Lohnjournal.
   *
   * Modi:
   * - 'append'  → neue Position wird an die bestehenden angehängt (nächste Position-Nr.)
   * - 'replace' → alle bestehenden Positionen werden gelöscht, danach neue Position eingefügt
   *
   * Sicherheits-Guards:
   * - Nur Rechnungen mit Status 'entwurf' werden verändert. Freigegebene/versendete/importierte
   *   Rechnungen werden ohne Schreibvorgang abgelehnt.
   * - Wenn kein Preis hinterlegt ist, wird die Position trotzdem mit Einzelpreis 0 angelegt
   *   (manuelle Nachpflege durch den Nutzer, identisch zur Logik in createDraft).
   */
  populateDraftFromPayroll: async (
    invoiceId: string,
    anzahlArbeitnehmer: number,
    options: { mode: 'append' | 'replace' }
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // 1) Rechnung laden und Status prüfen
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('id, status, customer_id, year, month')
        .eq('id', invoiceId)
        .single();

      if (invoiceError || !invoice) {
        return { success: false, error: 'Rechnung nicht gefunden' };
      }

      if (invoice.status !== 'entwurf') {
        return { success: false, error: 'Nur Entwürfe dürfen verändert werden. Diese Rechnung ist bereits freigegeben oder versendet.' };
      }

      if (!invoice.customer_id) {
        return { success: false, error: 'Rechnung hat keinen zugeordneten Kunden' };
      }

      // 2) Preis des Kunden laden (für Sammelrechnungen ist die Logik identisch:
      //    der Preis wird aus dem Customer-Pricing geladen, das beim Auftraggeber/Kunden hinterlegt ist)
      const pricing = await invoiceService.getCurrentPricing(invoice.customer_id);
      const einzelpreis = pricing ? Number(pricing.preis_pro_arbeitnehmer) : 0;
      const beschreibung = `Lohnabrechnungen ${String(invoice.month).padStart(2, '0')}/${invoice.year}`;

      // 3) Modus 'replace': alle bestehenden Positionen löschen
      if (options.mode === 'replace') {
        const { error: deleteError } = await supabase
          .from('invoice_line_items')
          .delete()
          .eq('invoice_id', invoiceId);

        if (deleteError) {
          console.error('[InvoiceService] Fehler beim Löschen bestehender Positionen:', deleteError);
          return { success: false, error: 'Bestehende Positionen konnten nicht gelöscht werden' };
        }

        // Nach 'replace' sind keine Positionen mehr vorhanden → die neue Position wird Position 1.
        // Wir nutzen den Helper, der diesen Fall mit erledigt (verschiebt 0 Positionen, fügt an Position 1 ein).
      }

      // 4) Lohn-Position an Position 1 einfügen (im 'append'-Modus werden bestehende
      //    Positionen automatisch um +1 verschoben). So liegt die Lohnabrechnung
      //    immer ganz oben in der Rechnung – auch wenn die DATEV-Weiterberechnung
      //    den Entwurf bereits mit Posten gefüllt hatte.
      const insertResult = await invoiceService.insertLohnPositionAtTop(
        invoiceId,
        beschreibung,
        anzahlArbeitnehmer,
        einzelpreis
      );

      if (!insertResult.success) {
        return { success: false, error: insertResult.error };
      }

      console.log(`[InvoiceService] Lohnjournal-Position (${options.mode}) für Rechnung ${invoiceId} ergänzt: ${anzahlArbeitnehmer} AN à ${einzelpreis}€`);
      return { success: true };

    } catch (error) {
      console.error('[InvoiceService] Unerwarteter Fehler in populateDraftFromPayroll:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  // ============================================
  // ENTWURF ERSTELLEN (automatisch nach Finalisierung)
  // ============================================

  /**
   * Erstellt automatisch einen Rechnungsentwurf nach der Finalisierung
   * 
   * Wird aus handleFileUpload in useDeadlines aufgerufen.
   * Läuft non-blocking – Fehler hier blockieren die Finalisierung nicht.
   */
  createDraft: async (input: CreateInvoiceDraftInput): Promise<{ success: boolean; invoiceId?: string; error?: string }> => {
    try {
      const { customerId, year, month, anzahlArbeitnehmer } = input;

      console.log(`[InvoiceService] Erstelle Entwurf für Kunde ${customerId}, ${month}/${year}`);

      // Kundendaten laden
      const { data: customer } = await supabase
        .from('customers')
        .select(`
          *,
          zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
            full_name
          )
        `)
        .eq('id', customerId)
        .single();

      if (!customer) {
        return { success: false, error: 'Kunde nicht gefunden' };
      }

      // ============================================
      // SAMMELRECHNUNG: Wenn auftraggeber_id gesetzt → Position beim Auftraggeber hinzufügen
      // ============================================
      if (customer.auftraggeber_id) {
        return await invoiceService.createOrAppendSammelrechnung({
          auftraggeberId: customer.auftraggeber_id,
          unterKundeCompany: customer.company_rechnung || customer.company,
          year,
          month,
          anzahlArbeitnehmer
        });
      }

      // ============================================
      // EINZELRECHNUNG: Normale Logik (Kunde ohne Auftraggeber)
      // ============================================

      // Prüfe ob bereits ein Entwurf existiert
      const { data: existing } = await supabase
        .from('invoices')
        .select('id')
        .eq('customer_id', customerId)
        .eq('year', year)
        .eq('month', month)
        .maybeSingle();

      if (existing) {
        console.log('[InvoiceService] Entwurf existiert bereits:', existing.id);

        // Wenn der Entwurf z.B. von der DATEV-Weiterberechnung bereits angelegt wurde,
        // ist die Lohnabrechnungs-Position noch nicht vorhanden. Wir fügen sie an
        // Position 1 ein (mit Verschiebung der bestehenden Posten), damit die
        // Lohnabrechnung immer als oberste Rechnungsposition erscheint.
        if (anzahlArbeitnehmer > 0) {
          const beschreibung = `Lohnabrechnungen ${String(month).padStart(2, '0')}/${year}`;

          // Duplikat-Schutz: prüfen, ob die Lohn-Position bereits existiert
          const { data: existingLohnPos } = await supabase
            .from('invoice_line_items')
            .select('id')
            .eq('invoice_id', existing.id)
            .eq('beschreibung', beschreibung)
            .maybeSingle();

          if (!existingLohnPos) {
            const pricing = await invoiceService.getCurrentPricing(customerId);
            const einzelpreis = pricing ? Number(pricing.preis_pro_arbeitnehmer) : 0;

            const insertResult = await invoiceService.insertLohnPositionAtTop(
              existing.id,
              beschreibung,
              anzahlArbeitnehmer,
              einzelpreis
            );

            if (!insertResult.success) {
              console.error('[InvoiceService] Lohn-Position konnte nicht in bestehenden Entwurf eingefügt werden:', insertResult.error);
            }
          }
        }

        return { success: true, invoiceId: existing.id };
      }

      // Preis laden
      const pricing = await invoiceService.getCurrentPricing(customerId);
      if (!pricing) {
        console.warn(`[InvoiceService] Kein Preis für Kunde ${customerId} hinterlegt – Entwurf wird ohne Positionen erstellt`);
      }

      // Einstellungen laden (für USt-Satz)
      const settings = await invoiceService.getSettings();
      const ustSatz = customer.reverse_charge ? 0 : (settings?.ust_satz ?? 19);

      // Leistungszeitraum berechnen
      const letzterTag = new Date(year, month, 0).getDate(); // Letzter Tag des Monats
      const leistungszeitraumVon = `${year}-${String(month).padStart(2, '0')}-01`;
      const leistungszeitraumBis = `${year}-${String(month).padStart(2, '0')}-${String(letzterTag).padStart(2, '0')}`;

      // Beträge berechnen (falls Preis vorhanden)
      const einzelpreis = pricing ? Number(pricing.preis_pro_arbeitnehmer) : 0;
      const gesamtpreis = Math.round(anzahlArbeitnehmer * einzelpreis * 100) / 100;
      const ustBetrag = Math.round(gesamtpreis * ustSatz) / 100;
      const bruttoBetrag = Math.round((gesamtpreis + ustBetrag) * 100) / 100;

      // Ansprechpartner aus zuständigem Mitarbeiter
      const ansprechpartner = customer.zustaendiger_mitarbeiter?.full_name || null;

      // Empfänger-Name zusammensetzen. Ein abweichender Rechnungsempfänger am
      // Kunden hat Vorrang vor dem Ansprechpartner (siehe
      // buildRechnungsEmpfaengerPerson).
      const empfaengerPerson = buildRechnungsEmpfaengerPerson(customer);

      // Rechnung erstellen
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          customer_id: customerId,
          year,
          month,
          status: 'entwurf',
          leistungszeitraum_von: leistungszeitraumVon,
          leistungszeitraum_bis: leistungszeitraumBis,
          ansprechpartner_name: ansprechpartner,
          netto_betrag: gesamtpreis,
          ust_satz: ustSatz,
          ust_betrag: ustBetrag,
          brutto_betrag: bruttoBetrag,
          empfaenger_firma: customer.company_rechnung || customer.company,
          empfaenger_anrede: empfaengerPerson.anrede,
          empfaenger_vorname: empfaengerPerson.vorname,
          empfaenger_nachname: empfaengerPerson.nachname,
          empfaenger_strasse: customer.strasse || null,
          empfaenger_plz: customer.plz || null,
          empfaenger_ort: customer.ort || null,
          empfaenger_land: customer.land || 'Deutschland',
          empfaenger_kundennummer: customer.kundennummer_rechnung || null
        })
        .select()
        .single();

      if (invoiceError) {
        console.error('[InvoiceService] Fehler beim Erstellen der Rechnung:', invoiceError);
        return { success: false, error: invoiceError.message };
      }

      // Position hinzufügen (auch ohne Preis – Einzelpreis dann 0, manuell nachtragbar)
      if (anzahlArbeitnehmer > 0) {
        const beschreibung = `Lohnabrechnungen ${String(month).padStart(2, '0')}/${year}`;

        await supabase
          .from('invoice_line_items')
          .insert({
            invoice_id: invoice.id,
            position: 1,
            beschreibung,
            menge: anzahlArbeitnehmer,
            einheit: 'Stk',
            einzelpreis,
            gesamtpreis
          });
      }

      console.log(`[InvoiceService] Entwurf erstellt: ${invoice.id}`);
      return { success: true, invoiceId: invoice.id };

    } catch (error) {
      console.error('[InvoiceService] Unerwarteter Fehler beim Entwurf:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unbekannter Fehler' 
      };
    }
  },

  /**
   * Sammelrechnung: Sucht/erstellt eine Rechnung beim Auftraggeber und fügt eine Position für den Unter-Kunden hinzu.
   * 
   * - Preis kommt aus customer_pricing des AUFTRAGGEBERS (nicht des Unter-Kunden)
   * - Position: "FIRMA GmbH – Lohnabrechnungen 03/2026"
   * - Beträge werden nach jeder neuen Position via recalculateInvoiceTotals aktualisiert
   */
  createOrAppendSammelrechnung: async (input: {
    auftraggeberId: string;
    unterKundeCompany: string;
    year: number;
    month: number;
    anzahlArbeitnehmer: number;
  }): Promise<{ success: boolean; invoiceId?: string; error?: string }> => {
    try {
      const { auftraggeberId, unterKundeCompany, year, month, anzahlArbeitnehmer } = input;

      console.log(`[InvoiceService] Sammelrechnung: ${unterKundeCompany} → Auftraggeber ${auftraggeberId}, ${month}/${year}`);

      // Auftraggeber-Daten laden
      const { data: auftraggeber } = await supabase
        .from('customers')
        .select(`
          *,
          zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
            full_name
          )
        `)
        .eq('id', auftraggeberId)
        .single();

      if (!auftraggeber) {
        return { success: false, error: `Auftraggeber ${auftraggeberId} nicht gefunden` };
      }

      // Bestehende Sammelrechnung beim Auftraggeber suchen (gleicher Monat/Jahr, statusunabhängig).
      // Duplikat-Schutz: Existiert für den Monat bereits eine Rechnung, die NICHT mehr im
      // Status 'entwurf' ist, wird KEINE neue Rechnung angelegt und KEINE Position an die
      // bereits finalisierte Rechnung angehängt. Der Aufrufer erhält einen Fehler (Variante B).
      let sammelrechnungId: string;

      const { data: existingInvoices } = await supabase
        .from('invoices')
        .select('id, status')
        .eq('customer_id', auftraggeberId)
        .eq('year', year)
        .eq('month', month)
        .limit(100000);

      const existingSammelrechnung = (existingInvoices || []).find(inv => inv.status === 'entwurf') || null;

      if (existingSammelrechnung) {
        sammelrechnungId = existingSammelrechnung.id;
        console.log(`[InvoiceService] Bestehende Sammelrechnung gefunden: ${sammelrechnungId}`);
      } else if (existingInvoices && existingInvoices.length > 0) {
        const vorhandenerStatus = existingInvoices[0].status;
        const fehlertext = `Für ${String(month).padStart(2, '0')}/${year} existiert beim Auftraggeber bereits eine Sammelrechnung (Status: ${vorhandenerStatus}). Es wurde keine neue Rechnung angelegt und keine Position für ${unterKundeCompany} hinzugefügt – bitte manuell prüfen.`;
        console.warn(`[InvoiceService] ${fehlertext}`);
        return { success: false, error: fehlertext };
      } else {
        // Neue Sammelrechnung erstellen (mit Auftraggeber-Adressdaten)
        const settings = await invoiceService.getSettings();
        const ustSatz = auftraggeber.reverse_charge ? 0 : (settings?.ust_satz ?? 19);

        const letzterTag = new Date(year, month, 0).getDate();
        const leistungszeitraumVon = `${year}-${String(month).padStart(2, '0')}-01`;
        const leistungszeitraumBis = `${year}-${String(month).padStart(2, '0')}-${String(letzterTag).padStart(2, '0')}`;

        const ansprechpartner = auftraggeber.zustaendiger_mitarbeiter?.full_name || null;
        // Auch bei der Sammelrechnung gilt: ein abweichender Rechnungsempfänger
        // am Auftraggeber hat Vorrang vor dessen Ansprechpartner.
        const empfaengerPerson = buildRechnungsEmpfaengerPerson(auftraggeber);

        const { data: neueSammelrechnung, error: createError } = await supabase
          .from('invoices')
          .insert({
            customer_id: auftraggeberId,
            year,
            month,
            status: 'entwurf',
            leistungszeitraum_von: leistungszeitraumVon,
            leistungszeitraum_bis: leistungszeitraumBis,
            ansprechpartner_name: ansprechpartner,
            netto_betrag: 0,
            ust_satz: ustSatz,
            ust_betrag: 0,
            brutto_betrag: 0,
            empfaenger_firma: auftraggeber.company_rechnung || auftraggeber.company,
            empfaenger_anrede: empfaengerPerson.anrede,
            empfaenger_vorname: empfaengerPerson.vorname,
            empfaenger_nachname: empfaengerPerson.nachname,
            empfaenger_strasse: auftraggeber.strasse || null,
            empfaenger_plz: auftraggeber.plz || null,
            empfaenger_ort: auftraggeber.ort || null,
            empfaenger_land: auftraggeber.land || 'Deutschland',
            empfaenger_kundennummer: auftraggeber.kundennummer_rechnung || null
          })
          .select()
          .single();

        if (createError || !neueSammelrechnung) {
          console.error('[InvoiceService] Fehler beim Erstellen der Sammelrechnung:', createError);
          return { success: false, error: createError?.message || 'Sammelrechnung konnte nicht erstellt werden' };
        }

        sammelrechnungId = neueSammelrechnung.id;
        console.log(`[InvoiceService] Neue Sammelrechnung erstellt: ${sammelrechnungId}`);
      }

      // Prüfe ob bereits eine Position für diesen Unter-Kunden existiert (Duplikat-Schutz)
      const beschreibung = `${unterKundeCompany} – Lohnabrechnungen ${String(month).padStart(2, '0')}/${year}`;

      const { data: existingPosition } = await supabase
        .from('invoice_line_items')
        .select('id')
        .eq('invoice_id', sammelrechnungId)
        .eq('beschreibung', beschreibung)
        .maybeSingle();

      if (existingPosition) {
        console.log(`[InvoiceService] Position für ${unterKundeCompany} existiert bereits in Sammelrechnung`);
        return { success: true, invoiceId: sammelrechnungId };
      }

      // Preis aus customer_pricing des AUFTRAGGEBERS laden
      const auftraggeberPricing = await invoiceService.getCurrentPricing(auftraggeberId);
      const einzelpreis = auftraggeberPricing ? Number(auftraggeberPricing.preis_pro_arbeitnehmer) : 0;
      const gesamtpreis = Math.round(anzahlArbeitnehmer * einzelpreis * 100) / 100;

      // Nächste Positionsnummer ermitteln
      const { data: existingItems } = await supabase
        .from('invoice_line_items')
        .select('position')
        .eq('invoice_id', sammelrechnungId)
        .order('position', { ascending: false })
        .limit(1);

      const nextPosition = existingItems && existingItems.length > 0
        ? existingItems[0].position + 1
        : 1;

      // Position hinzufügen
      if (anzahlArbeitnehmer > 0) {
        await supabase
          .from('invoice_line_items')
          .insert({
            invoice_id: sammelrechnungId,
            position: nextPosition,
            beschreibung,
            menge: anzahlArbeitnehmer,
            einheit: 'Stk',
            einzelpreis,
            gesamtpreis
          });

        // Rechnungsbeträge neu berechnen
        await invoiceService.recalculateInvoiceTotals(sammelrechnungId);
      }

      console.log(`[InvoiceService] Sammelrechnung ${sammelrechnungId}: Position für ${unterKundeCompany} hinzugefügt (${anzahlArbeitnehmer} AN, ${einzelpreis}€/AN)`);
      return { success: true, invoiceId: sammelrechnungId };

    } catch (error) {
      console.error('[InvoiceService] Fehler bei Sammelrechnung:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  // ============================================
  // VALIDIERUNG VOR FREIGABE
  // ============================================

  /**
   * Prüft ob eine Rechnung freigegeben werden darf.
   * Bedingungen:
   * - Mindestens eine Position vorhanden
   * - Keine Position mit Einzelpreis 0,00 €
   * - Vollständige Anschrift des Leistungsempfängers im Kundenstamm
   *
   * Die Anschriftsprüfung spiegelt die serverseitige Prüfung in
   * netlify/functions/send-invoice.js. Sie existiert hier zusätzlich, damit
   * der Nutzer die Meldung sofort sieht und damit approveInvoice abgedeckt ist,
   * das die Rechnungsnummer lokal vergibt und die Netlify-Function nicht aufruft.
   */
  validateInvoiceForApproval: async (invoiceId: string): Promise<{ valid: boolean; error?: string }> => {
    const { data: lineItems, error } = await supabase
      .from('invoice_line_items')
      .select('id, einzelpreis, beschreibung')
      .eq('invoice_id', invoiceId);

    if (error) {
      return { valid: false, error: 'Rechnungspositionen konnten nicht geladen werden' };
    }

    if (!lineItems || lineItems.length === 0) {
      return { valid: false, error: 'Rechnung hat keine Positionen. Bitte mindestens eine Position hinzufügen.' };
    }

    const zeroItems = lineItems.filter(item => Number(item.einzelpreis) === 0);
    if (zeroItems.length > 0) {
      return { valid: false, error: 'Rechnung enthält Positionen mit Einzelpreis 0,00 €. Bitte alle Preise eintragen.' };
    }

    // Pflichtangabe nach § 14 Abs. 4 Satz 1 Nr. 1 UStG: vollständige Anschrift
    // des Leistungsempfängers. Maßgeblich ist der Kundenstamm, weil send-invoice.js
    // die Empfängerfelder der Rechnung unmittelbar vor der PDF-Erzeugung von dort
    // überschreibt.
    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .select('customer_id')
      .eq('id', invoiceId)
      .single();

    if (invoiceError || !invoice) {
      return { valid: false, error: 'Rechnung konnte nicht geladen werden' };
    }

    if (!invoice.customer_id) {
      return { valid: false, error: 'Rechnung hat keinen zugeordneten Kunden' };
    }

    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .select('company, company_rechnung, strasse, plz, ort')
      .eq('id', invoice.customer_id)
      .single();

    if (customerError || !customer) {
      return { valid: false, error: 'Kundendaten konnten nicht geladen werden' };
    }

    const fehlendeAdressfelder: string[] = [];
    if (!String(customer.strasse || '').trim()) fehlendeAdressfelder.push('Straße');
    if (!String(customer.plz || '').trim()) fehlendeAdressfelder.push('PLZ');
    if (!String(customer.ort || '').trim()) fehlendeAdressfelder.push('Ort');

    if (fehlendeAdressfelder.length > 0) {
      const firma = customer.company_rechnung || customer.company;
      return {
        valid: false,
        error: `Rechnungsanschrift unvollständig, es fehlt: ${fehlendeAdressfelder.join(', ')}. Bitte die Anschrift beim Kunden ${firma} im Kundenstamm ergänzen. Ohne vollständige Anschrift des Leistungsempfängers ist der Beleg nach § 14 Abs. 4 Satz 1 Nr. 1 UStG formal fehlerhaft und berechtigt den Kunden nicht zum Vorsteuerabzug.`
      };
    }

    return { valid: true };
  },

  // ============================================
  // POSITIONS-RENUMBERING VOR FESTSCHREIBUNG
  // ============================================

  /**
   * Nummeriert alle Positionen einer Rechnung lückenlos auf 1..N um.
   *
   * Sortiert nach aktueller position aufsteigend und schreibt 1..N. Wird unmittelbar
   * vor dem Festschreiben (Status-Wechsel von 'entwurf' auf 'freigegeben'/'versendet')
   * aufgerufen, damit eventuelle Lücken (z.B. nach dem Löschen einer Position im
   * Entwurf) auf der finalen Rechnung nicht mehr auftauchen.
   *
   * Update-Reihenfolge: Wir schreiben zunächst alle Items in einen temporären
   * Offset-Bereich (1000 + originalPosition) und in einem zweiten Schritt auf die
   * finalen 1..N. Dadurch sind Kollisionen auch dann ausgeschlossen, falls in der
   * Zukunft ein UNIQUE-Constraint auf (invoice_id, position) eingeführt wird.
   *
   * Beträge werden NICHT neu berechnet, weil sich nur die Positionsnummern ändern.
   */
  renumberLineItemsSequentially: async (invoiceId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: items, error: loadError } = await supabase
        .from('invoice_line_items')
        .select('id, position')
        .eq('invoice_id', invoiceId)
        .order('position', { ascending: true });

      if (loadError) {
        console.error('[InvoiceService] Fehler beim Laden der Positionen für Renumbering:', loadError);
        return { success: false, error: 'Positionen konnten nicht geladen werden' };
      }

      if (!items || items.length === 0) {
        return { success: true };
      }

      // Prüfen, ob bereits lückenlos 1..N – dann nichts zu tun (No-Op)
      const alreadySequential = items.every((item, index) => item.position === index + 1);
      if (alreadySequential) {
        return { success: true };
      }

      // Phase 1: in temporären Offset-Bereich schreiben (kollisionsfrei auch
      // bei künftigem UNIQUE-Constraint auf (invoice_id, position))
      for (const item of items) {
        const { error: phase1Error } = await supabase
          .from('invoice_line_items')
          .update({
            position: item.position + 1000,
            updated_at: new Date().toISOString()
          })
          .eq('id', item.id);

        if (phase1Error) {
          console.error('[InvoiceService] Fehler beim Renumbering (Phase 1):', phase1Error);
          return { success: false, error: 'Positionen konnten nicht umnummeriert werden (Phase 1)' };
        }
      }

      // Phase 2: finale Positionen 1..N in der ursprünglichen Sortierreihenfolge
      for (let i = 0; i < items.length; i++) {
        const { error: phase2Error } = await supabase
          .from('invoice_line_items')
          .update({
            position: i + 1,
            updated_at: new Date().toISOString()
          })
          .eq('id', items[i].id);

        if (phase2Error) {
          console.error('[InvoiceService] Fehler beim Renumbering (Phase 2):', phase2Error);
          return { success: false, error: 'Positionen konnten nicht umnummeriert werden (Phase 2)' };
        }
      }

      console.log(`[InvoiceService] Rechnung ${invoiceId}: ${items.length} Positionen lückenlos umnummeriert`);
      return { success: true };
    } catch (error) {
      console.error('[InvoiceService] Unerwarteter Fehler beim Renumbering:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unbekannter Fehler' };
    }
  },

  // ============================================
  // FREIGABE (Rechnungsnummer vergeben)
  // ============================================

  /**
   * Generiert eine PDF-Vorschau der Rechnung (ohne Versand, ohne Statusänderung)
   * Bei Entwürfen: "ENTWURF"-Wasserzeichen statt Rechnungsnummer
   * Bei freigegebenen/versendeten: Normales PDF mit Rechnungsnummer
   *
   * Bei Entwürfen werden vor der PDF-Generierung die Positionen lückenlos auf
   * 1..N umnummeriert (analog zu den Approve-Pfaden). Damit verschwinden
   * Altlasten aus Vor-Deploy-Zuständen spätestens beim ersten PDF-Aufruf.
   * Festgeschriebene Rechnungen (freigegeben/versendet/importiert) werden
   * nicht angefasst.
   */
  previewPdf: async (invoiceId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Status laden, um zu entscheiden, ob renumbered werden soll.
      const { data: statusRow, error: statusError } = await supabase
        .from('invoices')
        .select('status')
        .eq('id', invoiceId)
        .single();

      if (statusError) {
        console.error('[InvoiceService] Status der Rechnung konnte nicht ermittelt werden:', statusError);
        // Renumbering wird übersprungen; PDF-Vorschau läuft trotzdem.
      } else if (statusRow?.status === 'entwurf') {
        const renumberResult = await invoiceService.renumberLineItemsSequentially(invoiceId);
        if (!renumberResult.success) {
          console.error('[InvoiceService] Renumbering vor PDF-Vorschau fehlgeschlagen:', renumberResult.error);
          // Fehler nur protokollieren – die PDF-Vorschau soll trotzdem starten.
        }
      }

      const response = await netlifyFetch('/.netlify/functions/send-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: invoiceId, preview_only: true })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return { success: false, error: result.error || 'PDF-Vorschau fehlgeschlagen' };
      }

      // Base64-PDF in Blob umwandeln und im Browser öffnen
      const byteCharacters = atob(result.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      return { success: true };
    } catch (error) {
      console.error('[InvoiceService] Fehler bei PDF-Vorschau:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Gibt eine Rechnung frei und versendet sie:
   * 1. Netlify Function: Rechnungsnummer vergeben → PDF generieren → E-Mail senden
   * 2. Alles server-seitig, atomar
   */
  approveAndSend: async (invoiceId: string): Promise<{ success: boolean; rechnungsnummer?: string; emailSent?: boolean; versendetAn?: string; error?: string }> => {
    try {
      // Validierung: Keine Positionen mit Einzelpreis 0
      const validation = await invoiceService.validateInvoiceForApproval(invoiceId);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Positionen lückenlos auf 1..N umnummerieren, bevor die Rechnung
      // festgeschrieben wird. Lücken durch gelöschte Positionen im Entwurf
      // verschwinden so spätestens beim Festschreiben.
      const renumberResult = await invoiceService.renumberLineItemsSequentially(invoiceId);
      if (!renumberResult.success) {
        return { success: false, error: renumberResult.error };
      }

      const response = await netlifyFetch('/.netlify/functions/send-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: invoiceId })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return { 
          success: false, 
          error: result.error || 'Freigabe fehlgeschlagen' 
        };
      }

      // DATEV-Debitorennummer für den Empfänger sicherstellen (idempotent;
      // vergibt fortlaufend eine neue Nummer, falls der Kunde noch keine hat).
      // Fehler hier blockieren den bereits abgeschlossenen Versand nicht – das
      // DATEV-Export-Schutznetz fängt eine fehlende Nummer sichtbar ab.
      const { error: debitorError } = await supabase.rpc('assign_debitor_number_for_invoice', {
        p_invoice_id: invoiceId
      });
      if (debitorError) {
        console.error('[InvoiceService] Debitorennummer konnte nicht vergeben werden:', debitorError.message);
      }

      return {
        success: true,
        rechnungsnummer: result.rechnungsnummer,
        emailSent: result.emailSent,
        versendetAn: result.versendetAn
      };
    } catch (error) {
      console.error('[InvoiceService] Fehler bei Freigabe & Versand:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Gibt eine Rechnung frei und lädt sie als PDF herunter (ohne E-Mail-Versand):
   * 1. Netlify Function: Rechnungsnummer vergeben → PDF generieren → Status 'freigegeben'
   * 2. PDF wird als Base64 zurückgegeben und im Browser heruntergeladen
   * 3. Kein E-Mail-Versand, Status bleibt auf 'freigegeben'
   */
  approveAndDownload: async (invoiceId: string): Promise<{ success: boolean; rechnungsnummer?: string; error?: string }> => {
    try {
      // Validierung: Keine Positionen mit Einzelpreis 0
      const validation = await invoiceService.validateInvoiceForApproval(invoiceId);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Positionen lückenlos auf 1..N umnummerieren, bevor die Rechnung
      // festgeschrieben wird. Lücken durch gelöschte Positionen im Entwurf
      // verschwinden so spätestens beim Festschreiben.
      const renumberResult = await invoiceService.renumberLineItemsSequentially(invoiceId);
      if (!renumberResult.success) {
        return { success: false, error: renumberResult.error };
      }

      const response = await netlifyFetch('/.netlify/functions/send-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: invoiceId, download_only: true })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || 'Freigabe fehlgeschlagen'
        };
      }

      // DATEV-Debitorennummer für den Empfänger sicherstellen (idempotent;
      // vergibt fortlaufend eine neue Nummer, falls der Kunde noch keine hat).
      // Fehler hier blockieren die bereits abgeschlossene Freigabe nicht – das
      // DATEV-Export-Schutznetz fängt eine fehlende Nummer sichtbar ab.
      const { error: debitorError } = await supabase.rpc('assign_debitor_number_for_invoice', {
        p_invoice_id: invoiceId
      });
      if (debitorError) {
        console.error('[InvoiceService] Debitorennummer konnte nicht vergeben werden:', debitorError.message);
      }

      // PDF-Download im Browser auslösen
      if (result.pdf && result.filename) {
        const byteCharacters = atob(result.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      return {
        success: true,
        rechnungsnummer: result.rechnungsnummer
      };
    } catch (error) {
      console.error('[InvoiceService] Fehler bei Freigabe & Download:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Gibt eine Rechnung frei (nur lokal, ohne Versand):
   * 1. Rechnungsnummer vergeben (atomar via DB)
   * 2. Rechnungsdatum setzen
   * 3. Status auf 'freigegeben'
   */
  approveInvoice: async (invoiceId: string): Promise<{ success: boolean; rechnungsnummer?: string; error?: string }> => {
    try {
      // Validierung: Keine Positionen mit Einzelpreis 0
      const validation = await invoiceService.validateInvoiceForApproval(invoiceId);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Positionen lückenlos auf 1..N umnummerieren, bevor die Rechnung
      // festgeschrieben wird. Lücken durch gelöschte Positionen im Entwurf
      // verschwinden so spätestens beim Festschreiben.
      const renumberResult = await invoiceService.renumberLineItemsSequentially(invoiceId);
      if (!renumberResult.success) {
        return { success: false, error: renumberResult.error };
      }

      // Atomar: Nächste Rechnungsnummer holen und hochzählen
      const { data: settings, error: settingsError } = await supabase
        .from('invoice_settings')
        .select('id, invoice_prefix, current_invoice_number')
        .limit(1)
        .single();

      if (settingsError || !settings) {
        return { success: false, error: 'Rechnungseinstellungen nicht gefunden' };
      }

      const nextNumber = settings.current_invoice_number + 1;
      const rechnungsnummer = `${settings.invoice_prefix}${nextNumber}`;

      // Zähler hochzählen
      const { error: updateError } = await supabase
        .from('invoice_settings')
        .update({ 
          current_invoice_number: nextNumber,
          updated_at: new Date().toISOString()
        })
        .eq('id', settings.id)
        .eq('current_invoice_number', settings.current_invoice_number); // Optimistic Lock

      if (updateError) {
        return { success: false, error: 'Rechnungsnummer konnte nicht vergeben werden (Concurrent Update)' };
      }

      // Rechnung aktualisieren
      const { error: invoiceError } = await supabase
        .from('invoices')
        .update({
          status: 'freigegeben',
          rechnungsnummer,
          rechnungsdatum: new Date().toISOString().split('T')[0],
          updated_at: new Date().toISOString()
        })
        .eq('id', invoiceId)
        .eq('status', 'entwurf'); // Nur Entwürfe können freigegeben werden

      if (invoiceError) {
        return { success: false, error: invoiceError.message };
      }

      // DATEV-Debitorennummer für den Empfänger sicherstellen (idempotent;
      // vergibt fortlaufend eine neue Nummer, falls der Kunde noch keine hat).
      // Fehler hier blockieren die bereits erfolgte Freigabe nicht – das
      // DATEV-Export-Schutznetz fängt eine fehlende Nummer sichtbar ab.
      const { error: debitorError } = await supabase.rpc('assign_debitor_number_for_invoice', {
        p_invoice_id: invoiceId
      });
      if (debitorError) {
        console.error('[InvoiceService] Debitorennummer konnte nicht vergeben werden:', debitorError.message);
      }

      console.log(`[InvoiceService] Rechnung freigegeben: ${rechnungsnummer}`);
      return { success: true, rechnungsnummer };

    } catch (error) {
      console.error('[InvoiceService] Fehler bei Freigabe:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unbekannter Fehler' 
      };
    }
  },

  /**
   * Markiert eine Rechnung als versendet
   */
  markAsSent: async (invoiceId: string, versendetAn: string): Promise<boolean> => {
    const { error } = await supabase
      .from('invoices')
      .update({
        status: 'versendet',
        versendet_am: new Date().toISOString(),
        versendet_an: versendetAn,
        updated_at: new Date().toISOString()
      })
      .eq('id', invoiceId);

    if (error) {
      console.error('[InvoiceService] Fehler beim Markieren als versendet:', error);
      return false;
    }
    return true;
  },

  // ============================================
  // DATEV EXTF EXPORT
  // ============================================

  /**
   * Generiert einen DATEV EXTF Buchungsstapel-Export bis einschließlich des
   * gewählten Buchhaltungsmonats (monatsübergreifend).
   *
   * Verhalten:
   * - Lädt alle noch nicht exportierten, freigegebenen/versendeten Rechnungen
   *   mit (year, month) <= (gewählt). Bereits exportierte Rechnungen werden
   *   ausgeschlossen.
   * - Backend markiert die exportierten Rechnungen nach erfolgreichem CSV-Build
   *   mit datev_exportiert_am = NOW(). Schlägt diese Markierung fehl, wird die
   *   CSV NICHT ausgeliefert (Schutz vor doppeltem Import in DATEV).
   * - Im DATEV-Header steht ausschließlich der gewählte Buchhaltungsmonat;
   *   die zeitliche Zuordnung der Buchungen erfolgt pro Zeile via Leistungsdatum.
   *
   * Response-Felder:
   * - invoiceCount: Anzahl der in der CSV enthaltenen Buchungszeilen
   * - skippedCount: Anzahl der wegen fehlender DATEV-Debitorennummer
   *                 übersprungenen Rechnungen
   * - skipped:      Liste der übersprungenen Rechnungen ({ rechnungsnummer, firma }),
   *                 damit das Frontend sie sichtbar auflisten kann (Schutznetz)
   * - periods:      Liste der enthaltenen Belegmonate (YYYY-MM, sortiert),
   *                 z.B. ["2026-03", "2026-04"] – nützlich für UI-Feedback
   * - bookingMonth: Der gewählte Buchhaltungsmonat (YYYY-MM)
   */
  generateDatevExport: async (year: number, month: number): Promise<{
    success: boolean;
    invoiceCount?: number;
    skippedCount?: number;
    skipped?: { rechnungsnummer: string; firma: string }[];
    periods?: string[];
    bookingMonth?: string;
    error?: string;
  }> => {
    try {
      const response = await netlifyFetch('/.netlify/functions/generate-datev-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, month })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return { success: false, error: result.error || 'DATEV Export fehlgeschlagen' };
      }

      // Base64 → Blob → Download
      const byteCharacters = atob(result.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'text/csv;charset=windows-1252' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return {
        success: true,
        invoiceCount: result.invoiceCount,
        skippedCount: result.skippedCount,
        skipped: result.skipped,
        periods: result.periods,
        bookingMonth: result.bookingMonth
      };
    } catch (error) {
      console.error('[InvoiceService] Fehler beim DATEV Export:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  // ============================================
  // SEPA-LASTSCHRIFT XML EXPORT (pain.008.001.08)
  // ============================================

  /**
   * Generiert eine SEPA-Lastschrift XML-Datei (pain.008.001.08) für einen Monat.
   *
   * Filterung (analog zum DATEV-Export):
   * - Nur Rechnungen mit Status 'freigegeben' oder 'versendet'
   * - Nur Rechnungen mit vergebener Rechnungsnummer
   * - Nur Rechnungen mit ist_storno = false (Storno-Feature noch nicht vorhanden;
   *   sobald Stornos eingeführt werden, muss die Logik dort angepasst werden)
   * - Nur Kunden mit zahlungsart = 'lastschrift'
   * - Doppeleinzug-Schutz via Tabelle sepa_exports (Hard-Fail bei bereits
   *   exportierter Rechnung)
   *
   * Hard-Fail bei fehlenden Pflichtfeldern (Gläubiger-ID, Bank-IBAN/BIC,
   * Lastschrift-IBAN, Mandatsreferenz, Mandatsdatum, Brutto > 0).
   *
   * @param year             Abrechnungsjahr
   * @param month            Abrechnungsmonat (1-12)
   * @param reqdColltnDt     Gewünschtes Einzugsdatum im Format YYYY-MM-DD
   *                         (Default in der UI: heute + 3 Bankarbeitstage)
   */
  generateSepaXml: async (
    year: number,
    month: number,
    reqdColltnDt: string
  ): Promise<{ success: boolean; invoiceCount?: number; ctrlSum?: string; error?: string; validationErrors?: string[] }> => {
    try {
      const response = await netlifyFetch('/.netlify/functions/generate-sepa-xml', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, month, reqd_colltn_dt: reqdColltnDt })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || 'SEPA-Export fehlgeschlagen',
          validationErrors: result.validationErrors
        };
      }

      // Base64 → Blob → Download (XML, UTF-8)
      const byteCharacters = atob(result.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return {
        success: true,
        invoiceCount: result.invoiceCount,
        ctrlSum: result.ctrlSum
      };
    } catch (error) {
      console.error('[InvoiceService] Fehler beim SEPA-Export:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }
};
